import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Phone, MapPin, CalendarCheck, LogOut, Loader2, BadgePercent, Receipt, X } from "lucide-react";
import { useClientAuth } from "../context/ClientAuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ClientAccount() {
  const { client, ready, logout, refresh } = useClientAuth();
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loadingRes, setLoadingRes] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (ready && !client) navigate("/account/login", { replace: true });
  }, [ready, client, navigate]);

  useEffect(() => {
    if (client) {
      setForm({
        prenom: client.prenom || "",
        nom: client.nom || "",
        telephone: client.telephone || "",
        adresse: client.adresse || "",
        code_postal: client.code_postal || "",
        ville: client.ville || "",
        fiscal_email: client.fiscal_email || "",
        urssaf_consent: !!client.urssaf_consent,
      });
    }
  }, [client]);

  useEffect(() => {
    if (!client) return;
    api.get("/clients/my-reservations")
      .then((r) => setReservations(r.data))
      .catch(() => {})
      .finally(() => setLoadingRes(false));
  }, [client]);

  const cancelReservation = async (rid) => {
    if (!window.confirm("Confirmer l'annulation de cette réservation ?")) return;
    try {
      await api.post(`/clients/my-reservations/${rid}/cancel`);
      toast.success("Réservation annulée — créneaux libérés");
      const r = await api.get("/clients/my-reservations");
      setReservations(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!ready || !client) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-fegreen" />
      </div>
    );
  }

  const totalSpent = reservations
    .filter((r) => r.payment_status === "paid")
    .reduce((s, r) => s + (r.amount_after_credit || r.amount || 0), 0);
  const creditSaved = reservations
    .filter((r) => r.payment_status === "paid" && r.use_credit_impot)
    .reduce((s, r) => s + (r.amount || 0) * 0.5, 0);

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/clients/me", form);
      await refresh();
      setEditing(false);
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <section className="bg-fegray-light px-6 py-12 lg:py-16">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">
              — Espace client
            </p>
            <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark">
              Bonjour {client.prenom} 👋
            </h1>
            <p className="text-fegray mt-2">Bienvenue dans votre espace personnel FORM'ELEC.</p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="account-logout"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-red-200 text-red-500 hover:bg-red-50 transition-colors text-sm font-semibold self-start sm:self-auto"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <StatCard icon={CalendarCheck} label="Réservations" value={reservations.length} />
          <StatCard icon={Receipt} label="Total payé" value={`${totalSpent.toFixed(2)}€`} />
          <StatCard icon={BadgePercent} label="Crédit d'impôt cumulé" value={`${creditSaved.toFixed(2)}€`} accent />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Profile */}
          <div className="lg:col-span-2 fe-card p-7">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-fegray-dark">Mon profil</h2>
              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  data-testid="edit-profile-btn"
                  className="text-sm text-fegreen font-semibold hover:underline"
                >
                  Modifier
                </button>
              )}
            </div>

            {!editing ? (
              <div className="space-y-3">
                <Row icon={User} value={`${client.prenom} ${client.nom}`} />
                <Row icon={Mail} value={client.email} />
                <Row icon={Phone} value={client.telephone} />
                {client.adresse && (
                  <Row icon={MapPin} value={`${client.adresse}, ${client.code_postal} ${client.ville}`} />
                )}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <p className="text-xs uppercase tracking-wider text-fegray mb-2">Crédit d'impôt</p>
                  <p className="text-sm text-fegray-dark">
                    {client.fiscal_email ? (
                      <>Email fiscal : <span className="font-semibold">{client.fiscal_email}</span></>
                    ) : (
                      <span className="text-amber-600">Aucun email fiscal renseigné</span>
                    )}
                  </p>
                  <p className="text-xs text-fegray mt-1">
                    URSSAF : {client.urssaf_consent ? "✅ Autorisation donnée" : "❌ Non autorisée"}
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={saveProfile} className="space-y-3">
                <Mini label="Prénom" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} />
                <Mini label="Nom" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} />
                <Mini label="Téléphone" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} />
                <Mini label="Adresse" value={form.adresse} onChange={(v) => setForm({ ...form, adresse: v })} />
                <div className="grid grid-cols-3 gap-2">
                  <Mini label="CP" value={form.code_postal} onChange={(v) => setForm({ ...form, code_postal: v })} />
                  <div className="col-span-2">
                    <Mini label="Ville" value={form.ville} onChange={(v) => setForm({ ...form, ville: v })} />
                  </div>
                </div>
                <Mini label="Email fiscal" type="email" value={form.fiscal_email} onChange={(v) => setForm({ ...form, fiscal_email: v })} />
                <label className="flex items-start gap-2 text-xs text-fegray-dark">
                  <input
                    type="checkbox"
                    checked={form.urssaf_consent}
                    onChange={(e) => setForm({ ...form, urssaf_consent: e.target.checked })}
                    className="mt-0.5 accent-fegreen"
                  />
                  J'autorise FORM'ELEC à utiliser mes données URSSAF.
                </label>
                <div className="flex gap-2 pt-3">
                  <button type="submit" className="btn-green flex-1" data-testid="save-profile">Enregistrer</button>
                  <button type="button" onClick={() => setEditing(false)} className="px-4 py-2.5 rounded-full border border-gray-200 text-fegray-dark text-sm font-semibold hover:bg-gray-50">
                    Annuler
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Reservations */}
          <div className="lg:col-span-3 fe-card p-7">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-fegray-dark">Mes réservations</h2>
              <Link to="/reservation" className="text-sm text-fegreen font-semibold hover:underline">
                + Nouvelle réservation
              </Link>
            </div>

            {loadingRes ? (
              <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-fegreen" /></div>
            ) : reservations.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-fegray text-sm mb-4">Vous n'avez encore aucune réservation.</p>
                <Link to="/reservation" className="btn-green inline-flex" data-testid="empty-go-reserve">
                  Réserver ma première séance
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {reservations.map((r) => {
                  const isCancellable = r.payment_status && r.payment_status !== "paid" && r.payment_status !== "cancelled";
                  // Compute hours-until-first-slot to enforce a 72h cancellation policy
                  const firstSlot = (r.slots || []).slice().sort((a, b) =>
                    (a.date + (a.time || "")).localeCompare(b.date + (b.time || ""))
                  )[0];
                  let hoursUntil = null;
                  if (firstSlot && firstSlot.date) {
                    const dt = new Date(`${firstSlot.date}T${firstSlot.time || "00:00"}:00Z`);
                    hoursUntil = (dt.getTime() - Date.now()) / 36e5;
                  }
                  const within72h = hoursUntil !== null && hoursUntil < 72;
                  const canCancel = isCancellable && !within72h;
                  return (
                    <div key={r.id} className="border border-gray-100 rounded-xl p-4" data-testid={`account-res-${r.id}`}>
                      <div className="flex flex-wrap justify-between items-start gap-3">
                        <div>
                          <div className="font-bold text-fegray-dark">{r.pack_name}</div>
                          <div className="text-xs text-fegray mt-1">
                            {(r.slots || []).length || 1} séance(s) · Première : {r.date_souhaitee} à {r.horaire}
                          </div>
                        </div>
                        <StatusBadge status={r.payment_status} />
                      </div>
                      <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-gray-100">
                        <span className="text-xs text-fegray">Montant payé</span>
                        <span className="text-sm font-bold text-fegreen">
                          {(r.amount_after_credit || r.amount).toFixed(2)}€
                        </span>
                      </div>
                      {canCancel && (
                        <button
                          onClick={() => cancelReservation(r.id)}
                          data-testid={`cancel-res-${r.id}`}
                          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-red-200 text-red-500 text-xs font-semibold hover:bg-red-50 hover:border-red-400 transition-colors"
                        >
                          <X className="w-3 h-3" /> Annuler cette réservation (paiement non confirmé)
                        </button>
                      )}
                      {isCancellable && within72h && (
                        <div
                          data-testid={`cancel-locked-${r.id}`}
                          className="mt-3 w-full text-center px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-medium leading-relaxed"
                        >
                          Annulation en ligne fermée — la séance est dans moins de 72h.<br />
                          Contactez Rosalie au <a href="tel:+33766253143" className="underline font-bold">07 66 25 31 43</a>.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div
      className={`rounded-2xl p-5 shadow-[0_4px_24px_rgba(0,0,0,0.06)] ${
        accent ? "bg-fegreen" : "bg-white"
      }`}
    >
      <div className={`flex items-center gap-3 ${accent ? "text-white/90" : "text-fegray"}`}>
        <Icon className={`w-5 h-5 ${accent ? "text-white" : "text-fegreen"}`} />
        <span className="text-xs uppercase tracking-wider font-semibold">{label}</span>
      </div>
      <div className={`text-2xl font-extrabold mt-2 ${accent ? "text-white" : "text-fegray-dark"}`}>{value}</div>
    </div>
  );
}

function Row({ icon: Icon, value }) {
  return (
    <div className="flex items-start gap-3 text-sm text-fegray-dark">
      <Icon className="w-4 h-4 text-fegreen mt-0.5 flex-shrink-0" />
      <span>{value}</span>
    </div>
  );
}

function Mini({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-fegray mb-1">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-fegray-dark focus:border-fegreen focus:outline-none"
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: { label: "Payée", cls: "bg-fegreen text-white" },
    pending: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
    unpaid: { label: "Non payée", cls: "bg-gray-100 text-fegray-dark" },
    cancelled: { label: "Annulée", cls: "bg-red-100 text-red-600" },
  };
  const cfg = map[status] || { label: status || "—", cls: "bg-gray-100 text-fegray-dark" };
  return <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${cfg.cls}`}>{cfg.label}</span>;
}
